/**
 * Vehicle Movement Timeline API Client
 *
 * Fetches the truth-layer movement timeline projection for a given vehicle.
 * Endpoint: GET /truth/vehicles/:vehicleId/movement
 *
 * Usage:
 *   const timeline = await fetchVehicleMovementTimeline({
 *     vehicleId: '01JEXAMPLE',
 *     from: '2026-01-15T00:00:00Z',
 *     to:   '2026-01-16T00:00:00Z',
 *   });
 */

import type {
  MovementTimelineQuery,
  VehicleMovementTimelineResponse,
  VehiclePositionsResponse,
} from '../types/vehicleMovementTimeline';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import { ZEECORE_BASE_URL } from '../config/api';

/**
 * Build the full URL for the movement timeline endpoint.
 */
export function buildMovementTimelineUrl(query: MovementTimelineQuery): string {
  const params = new URLSearchParams();
  params.append('from', query.from);
  params.append('to', query.to);
  if (query.asOf) params.append('asOf', query.asOf);
  if (query.includePoints !== undefined) {
    params.append('includePoints', String(query.includePoints));
  }
  const vehicleId = encodeURIComponent(query.vehicleId);
  return `${ZEECORE_BASE_URL}/truth/vehicles/${vehicleId}/movement?${params.toString()}`;
}

/**
 * Fetch the vehicle movement timeline from the truth layer.
 *
 * Uses authenticatedFetch to include JWT token in request headers.
 * The backend extracts organizationId from the JWT token.
 *
 * Throws on network error or non-2xx response with a descriptive message.
 */
export async function fetchVehicleMovementTimeline(
  query: MovementTimelineQuery,
): Promise<VehicleMovementTimelineResponse> {
  const url = buildMovementTimelineUrl(query);

  const response = await authenticatedFetch(url);

  if (!response.ok) {
    let detail = '';
    try {
      const body = await response.json();
      detail = body.error || body.message || JSON.stringify(body);
    } catch {
      detail = response.statusText;
    }
    throw new Error(
      `Movement timeline request failed (${response.status}): ${detail}`,
    );
  }

  return response.json();
}

/**
 * Fetch raw GPS positions for a vehicle over a time window.
 * Endpoint: GET /truth/vehicles/:vehicleId/positions?from=&to=
 */
export async function fetchVehiclePositions(
  vehicleId: string,
  from: string,
  to: string,
): Promise<VehiclePositionsResponse> {
  const params = new URLSearchParams({ from, to });
  const url = `${ZEECORE_BASE_URL}/truth/vehicles/${encodeURIComponent(vehicleId)}/positions?${params.toString()}`;

  const response = await authenticatedFetch(url);

  if (!response.ok) {
    let detail = '';
    try {
      const body = await response.json();
      detail = body.error || body.message || JSON.stringify(body);
    } catch {
      detail = response.statusText;
    }
    throw new Error(
      `Vehicle positions request failed (${response.status}): ${detail}`,
    );
  }

  return response.json();
}
